import { useState, useRef, MouseEvent, FC, Dispatch, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";

import Button from "@/components/Button";
import SearchBar from "@/components/SearchBar";
import Text from "@/components/Text";
import { ReactComponent as TopicsIcon } from "@/assets/icons/topics.svg";
import { ReactComponent as TopicsChangedIcon } from "@/assets/icons/topics-changed.svg";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { Tags } from "@/pages/AssignTopics/components/AttachmentsField";
import Tag from "@/components/Tag";
import { VideoTopicResource } from "@/types/resources";
import { api } from "@/utils/api";
import useDebounce from "@/hooks/useDebounce";
import { Inner, Box, Title, Item } from "@/components/Dropdown";
import { Controls, ControlsButton } from "@/components/Controls";

const StyledBox = styled(Box)`
  width: 320px;
`;

const StyledTitle = styled(Title)`
  padding: 0 16px;
  margin-bottom: 20px;
`;

const StyledText = styled(Text)`
  transition: color ease-out 0.2s;
  &:hover,
  &:active {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const Header = styled.div`
  padding: 0 16px;
`;

const TitleText = styled(Text)`
  margin: 0 14px;
  margin-top: 8px;
`;

const Capital = styled.span`
  text-transform: capitalize;
`;

export const NoTopics = styled.div`
  text-align: center;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const TopicsHolder = styled.div`
  position: relative;
  margin-left: auto;
`;

const StyledTopicsChangedIcon = styled(TopicsChangedIcon)`
  margin-top: -2px;
  margin-right: 5px !important;
`;

const StyledInner = styled(Inner)`
  padding: 0 4px 8px;
`;

const StyledTags = styled(Tags)`
  margin-bottom: 8px;
`;

const StyledSearchBar = styled(SearchBar)`
  margin-bottom: 8px;
`;

const CombinationsHolder = styled.div`
  margin-top: 16px;
`;

const Combo = styled.span`
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 20px;
  margin-left: 4px;
  font-weight: 400;
  font-size: 9px;
  line-height: 12px;
  padding: 0 4px;
  color: ${({ theme }) => theme.colors.black} !important;
`;

const StyledItem = styled(Item)`
  text-align: left;
  display: block;
  padding: 5px 14px;
  ${Text} {
    transition: color ease-out 0.2s;
    &:first-child {
      margin-bottom: 2px;
      span {
        color: ${({ theme }) => theme.colors.purple};
      }
    }
  }
  &:hover {
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
`;

const SelectionsHolder = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
  padding: 12px 2px;
  margin-bottom: 8px;
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledControls = styled(Controls)`
  flex-shrink: 0;
  margin-left: 16px;
`;

const SelectionsText = styled(Text)`
  font-weight: 400;
`;

interface Props {
  category?: string;
  topics: VideoTopicResource[];
  setTopics: Dispatch<React.SetStateAction<VideoTopicResource[]>>;
  className?: string;
  conditions?: { value: string; title: string }[];
  activeCondition?: { value: string; title: string };
  setActiveCondition?: Dispatch<
    React.SetStateAction<{ value: string; title: string }>
  >;
}

const TopicsFIlter: FC<Props> = ({
  setTopics,
  topics,
  category,
  className,
  conditions,
  activeCondition,
  setActiveCondition,
}) => {
  const { teamId } = useParams();

  const [showTopicsFilter, setShowTopicsFilter] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [suggestions, setSuggestions] = useState<VideoTopicResource[]>([]);

  const [combinations, setCombinations] = useState<VideoTopicResource[]>([]);

  const debouncedTopic = useDebounce(search, 400);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowTopicsFilter(false));

  const splitMatchedText = (str: string) => {
    const regExp = new RegExp(debouncedTopic, "gi");
    return str.replace(regExp, (match) => `<span>${match}</span>`);
  };

  useEffect(() => {
    if (debouncedTopic.length < 2) {
      setIsLoading(false);
      return setSuggestions([]);
    }

    setIsLoading(true);

    api
      .get<never, VideoTopicResource[]>("/v1/video-topics", {
        params: {
          ...(!!category && { category: category }),
          search: debouncedTopic,
        },
      })
      .then((suggestions) => {
        setSuggestions(suggestions);
        setIsLoading(false);
      });
  }, [debouncedTopic, category]);

  useEffect(() => {
    if (!suggestions.length) return setCombinations([]);
    api
      .get<never, VideoTopicResource[]>("/v1/video-topics/combinations", {
        params: {
          ...(teamId !== "all" && { team_id: teamId }),
          ...(!!category && { category: category }),
          topics: topics.map((topic) => topic.id).join(","),
        },
      })
      .then((combinations) => {
        setCombinations(combinations);
      });
  }, [category, suggestions, teamId, topics]);

  const onClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setTopics([]);
    setSearch("");
    setSuggestions([]);
    setCombinations([]);
    setShowTopicsFilter(false);
  };

  const onTopicRemove = (
    e: MouseEvent<HTMLButtonElement>,
    topicToRemove: VideoTopicResource
  ) => {
    e.stopPropagation();
    setTopics((prevTopics) =>
      prevTopics.filter((topic) => topic.id !== topicToRemove.id)
    );
  };

  const onTopicAdd = (
    e: MouseEvent<HTMLButtonElement>,
    topicToAdd: VideoTopicResource
  ) => {
    e.stopPropagation();
    setTopics((prevTopics) => [...prevTopics, topicToAdd]);
  };

  const noSuggestionsFound =
    debouncedTopic.length >= 2 && !!!suggestions.length && !isLoading;

  const filteredSuggestions = suggestions.filter(
    (suggestion) => !topics.map((t) => t.id).includes(suggestion.id)
  );

  const filteredCombinations = combinations.filter(
    (combination) => !topics.map((t) => t.id).includes(combination.id)
  );

  return (
    <TopicsHolder className={className} ref={ref}>
      <Button
        onClick={() => setShowTopicsFilter(!showTopicsFilter)}
        color="action"
        active={!!showTopicsFilter || !!topics.length}
        small
      >
        {!!topics.length ? <StyledTopicsChangedIcon /> : <TopicsIcon />}
        Topics
      </Button>
      {showTopicsFilter && (
        <StyledBox>
          <StyledTitle $color="black" $type="smallBold">
            Topics
            <StyledText
              as="button"
              $color="gray"
              $type="extraSmall"
              onClick={onClear}
            >
              Clear
            </StyledText>
          </StyledTitle>
          <Header>
            <StyledSearchBar value={search} setValue={setSearch} />
            {!!topics.length && (
              <StyledTags>
                {topics.map((topic) => {
                  return (
                    <Tag
                      key={topic.id}
                      onDelete={(e) => onTopicRemove(e, topic)}
                    >
                      {topic.title}
                      <Text $color="gray" $type="small">
                        &nbsp;(<Capital>{topic.category}</Capital> topic
                        {!!topic.team && ` · ${topic.team.name}`})
                      </Text>
                    </Tag>
                  );
                })}
              </StyledTags>
            )}
            {!!conditions?.length && !!setActiveCondition && (
              <SelectionsHolder>
                <SelectionsText $type="extraSmall">
                  By applying multiple topics, choose a combination type to show
                  results.
                </SelectionsText>
                <StyledControls>
                  {conditions.map((condition, index) => (
                    <ControlsButton
                      key={index}
                      active={condition.value === activeCondition!.value}
                      onClick={() => setActiveCondition(condition)}
                    >
                      {condition.title}
                    </ControlsButton>
                  ))}
                </StyledControls>
              </SelectionsHolder>
            )}
            {noSuggestionsFound && (
              <NoTopics>
                <Text $color="black" $type="smallBold">
                  No topics found
                </Text>
                <Text $color="gray" $type="smallMedium">
                  Topic may not been added to the system library.
                </Text>
              </NoTopics>
            )}
          </Header>
          <StyledInner>
            {!!filteredSuggestions.length && (
              <>
                <TitleText $type="extraSmall">Keyword suggestions</TitleText>
                {filteredSuggestions.map((suggestion) => (
                  <StyledItem
                    key={suggestion.id}
                    as="button"
                    onClick={(e: MouseEvent<HTMLButtonElement>) =>
                      onTopicAdd(e, suggestion)
                    }
                  >
                    <Text
                      dangerouslySetInnerHTML={{
                        __html: splitMatchedText(suggestion.title),
                      }}
                      $color="gray"
                      $type="smallBold"
                    />
                    <Text $color="gray" $type="extraSmall">
                      <Capital>{suggestion.category}</Capital> topic
                      {!!suggestion.team &&
                        ` · created by ${suggestion.team.name}`}
                    </Text>
                  </StyledItem>
                ))}
              </>
            )}
            {!!filteredCombinations.length && (
              <CombinationsHolder>
                <TitleText $type="extraSmall">Top combinations</TitleText>
                {filteredCombinations.map((combination) => (
                  <StyledItem
                    key={combination.id}
                    as="button"
                    onClick={(e: MouseEvent<HTMLButtonElement>) =>
                      onTopicAdd(e, combination)
                    }
                  >
                    <Text $color="gray" $type="smallBold">
                      {combination.title}
                      <Combo>Combo</Combo>
                    </Text>
                    <Text $color="gray" $type="extraSmall">
                      <Capital>{combination.category}</Capital> topic
                      {!!combination.team &&
                        ` · created by ${combination.team.name}`}
                    </Text>
                  </StyledItem>
                ))}
              </CombinationsHolder>
            )}
          </StyledInner>
        </StyledBox>
      )}
    </TopicsHolder>
  );
};

export default TopicsFIlter;
